import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

type LoginFormValues = { email: string; password: string };

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form] = Form.useForm<LoginFormValues>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as any)?.from?.pathname || '/';

  const onFinish = async (values: LoginFormValues) => {
    setError(null);
    setLoading(true);
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (e: any) {
      setError(e?.message || 'Login fallito');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 0,
        margin: 0,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          border: 'none',
          margin: 20,
        }}
        bodyStyle={{ padding: 40 }}
      >
        {/* Logo e Titolo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo_miglioriamo.png" alt="Logo Miglioriamo" style={{ width: 120, marginBottom: 16 }} />
          <Title level={2} style={{ margin: '0 0 8px', color: '#1a1a1a' }}>
            Benvenuto
          </Title>
          <Text type="secondary">Accedi al tuo account CRM</Text>
        </div>

        {/* Errore */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 24, borderRadius: 8 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {/* Form di login */}
        <Form<LoginFormValues>
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
          autoComplete="on"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Inserisci email!' },
              { type: 'email', message: 'Formato email non valido' },
            ]}
          >
            <Input
              type="email"
              autoComplete="email"
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="email@esempio.com"
              style={{ borderRadius: 10 }}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Inserisci la tua password!' }]}
          >
            <Input.Password
              autoComplete="current-password"
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              style={{ borderRadius: 10 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 48,
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
              }}
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </Form.Item>
        </Form>

        <Divider style={{ margin: '24px 0 0' }} />
      </Card>
    </div>
  );
};

export default Login;
